import React, { useState } from "react";
import { Container, ContainerBox } from "./styles";
import {
  Alert,
  Button,
  Snackbar,
  LinearProgress,
  Typography,
  Box,
} from "@mui/material";
import { sendDocuments } from "../../api/autenticoAPI";
import { Loading } from "../Loading/Loading";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SendIcon from "@mui/icons-material/Send";
import { StudentSearch } from "../common/StudentSearch";
import {
  AttachmentsArea,
  FormItens,
  PageTitle,
  ActionsArea,
  DividerAreaLeft,
  DividerAreaRight,
} from "../../pages/Announcements/styles";
import {
  uploadMultipleFiles,
  validateFileSize,
  formatFileSize
} from "../../utils/chunkedUpload";

export const SaveDocuments = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = React.useState(false);
  const [typeError, setTypeError] = useState("success");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [anexosSelecionados, setAnexosSelecionados] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Estados para upload em chunks
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [fileValidationErrors, setFileValidationErrors] = useState([]);

  // Callback ao selecionar aluno no componente StudentSearch
  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const errors = [];
    const validFiles = [];

    // Validar cada arquivo
    files.forEach(file => {
      const validation = validateFileSize(file, 500 * 1024 * 1024); // 500MB máximo
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push({
          fileName: file.name,
          error: validation.error
        });
      }
    });

    setFileValidationErrors(errors);
    setAnexosSelecionados([...anexosSelecionados, ...validFiles]);

    // Mostrar erros se houver
    if (errors.length > 0) {
      setErrorMessage(`Alguns arquivos foram rejeitados: ${errors.map(e => e.fileName).join(', ')}`);
      setTypeError("warning");
      setError(true);
    }
  };

  const resetFields = () => {
    // Não limpamos o aluno selecionado para facilitar múltiplos envios para o mesmo aluno
    // Mas limpamos os anexos
    setAnexosSelecionados([]);
    setUploadProgress(0);
    setIsUploading(false);
    setUploadStatus("");
    setFileValidationErrors([]);
  };

  const sendFile = async () => {
    if (anexosSelecionados.length === 0) {
      setErrorMessage("Nenhum arquivo selecionado!");
      setTypeError("error");
      setError(true);
      return;
    }
    // selectedStudent agora é um objeto { AlunoID, Nome }
    if (!selectedStudent || !selectedStudent.AlunoID) {
      setErrorMessage("Selecione um aluno antes de enviar os documentos.");
      setTypeError("error");
      setError(true);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus("Preparando upload...");

    try {
      const { AlunoID, Nome } = selectedStudent;

      // Determinar se usar upload em chunks ou direto
      const hasLargeFiles = anexosSelecionados.some(file => file.size > 5 * 1024 * 1024); // 5MB

      if (hasLargeFiles) {
        // Usar upload em chunks para arquivos grandes
        setUploadStatus("Fazendo upload em chunks...");

        const result = await uploadMultipleFiles(
          anexosSelecionados,
          "/documents/upload-chunks", // Endpoint para chunks (implementado no backend)
          { Nome, AlunoID },
          (progress, currentFile, totalFiles, fileName) => {
            setUploadProgress(progress);
            setUploadStatus(`Enviando ${fileName} (${currentFile}/${totalFiles})`);
          },
          2 // Máximo 2 uploads simultâneos
        );

        if (result.failedUploads > 0) {
          setErrorMessage(`Upload concluído com erros. ${result.successfulUploads} sucessos, ${result.failedUploads} falhas.`);
          setTypeError("warning");
        } else {
          setErrorMessage("Todos os documentos foram enviados com sucesso!");
          setTypeError("success");
        }
      } else {
        // Usar upload direto para arquivos pequenos
        setUploadStatus("Fazendo upload direto...");

        const fData = new FormData();
        anexosSelecionados.forEach((file) => {
          fData.append("documents", file);
        });
        fData.append("Nome", Nome);
        fData.append("AlunoID", AlunoID);

        const result = await sendDocuments(fData);
        if (result) {
          setErrorMessage("Documentos enviados com sucesso!");
          setTypeError("success");
        }
      }

      setError(true);
      resetFields();

    } catch (error) {
      console.error("Erro no upload:", error);
      setErrorMessage(`Erro no upload: ${error.message}`);
      setTypeError("error");
      setError(true);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus("");
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setError(false);
  };
  return (
    <>
      <Container>
        <ContainerBox>
          <PageTitle>Documentos</PageTitle>

          <FormItens encType="multipart/form-data">
            {/* Linha de Busca (Full Width) */}
            <Box sx={{ width: "100%", mb: 2 }}>
              <StudentSearch
                onSelect={handleStudentSelect}
                label="Digite o nome do aluno para consulta (mín. 3 caracteres)"
              />
            </Box>

            {/* Área de Ações e Lista de Arquivos */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* Botões de Ação (Lado a Lado) */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    backgroundColor: "#1E958C",
                    flex: '1 1 200px',
                    height: '56px',
                    '&:hover': { backgroundColor: "#17726b" }
                  }}
                >
                  Inserir Documentos
                  <input
                    type="file"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                    multiple
                  />
                </Button>

                <Button
                  onClick={() => {
                    sendFile();
                  }}
                  startIcon={<SendIcon />}
                  variant="contained"
                  sx={{
                    flex: '1 1 200px',
                    height: '56px'
                  }}
                  disabled={
                    selectedStudent === null ||
                    anexosSelecionados.length === 0 ||
                    isUploading
                  }
                >
                  {isUploading ? "Enviando..." : "Salvar documentos"}
                </Button>
              </Box>

              {/* Lista de Arquivos e Barra de Progresso */}
              <AttachmentsArea>
                {anexosSelecionados.length > 0 && (
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Arquivos selecionados ({anexosSelecionados.length}):
                  </Typography>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {anexosSelecionados.map((file, index) => (
                    <Box key={index} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      border: '1px solid #e0e0e0'
                    }}>
                      <CloudUploadIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {file.name} <strong>({formatFileSize(file.size)})</strong>
                      </Typography>
                      {file.size > 5 * 1024 * 1024 && (
                        <Typography variant="caption" color="warning.main">
                          (Chunked)
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Mostrar erros de validação */}
                {fileValidationErrors.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#fff4f4', borderRadius: 1 }}>
                    <Typography variant="body2" color="error" fontWeight="bold">
                      Arquivos rejeitados:
                    </Typography>
                    {fileValidationErrors.map((error, index) => (
                      <Typography key={index} variant="caption" color="error" display="block">
                        • {error.fileName}: {error.error}
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Barra de progresso */}
                {isUploading && (
                  <Box sx={{ mt: 2, width: '100%' }}>
                    <Typography variant="body2" gutterBottom>
                      {uploadStatus}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                    <Typography variant="caption" color="text.secondary" align="right" display="block">
                      {Math.round(uploadProgress)}%
                    </Typography>
                  </Box>
                )}
              </AttachmentsArea>

            </Box>
          </FormItens>

          <Snackbar
            open={error}
            autoHideDuration={5000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              variant="filled"
              onClose={handleClose}
              severity={typeError}
              sx={{ width: "100%" }}
            >
              {errorMessage}
            </Alert>
          </Snackbar>
        </ContainerBox>
      </Container>
      {loading && <Loading />}
    </>
  );
};
